/**
 * Weapon Utilities
 *
 * SRD weapon lookup from equipment strings.
 * Maps plain-text weapon names to their 5e stats for attack/damage calculations.
 */
// SRD weapon table
const SRD_WEAPONS = {
    // Simple melee
    'club': { damage: '1d4', damageType: 'bludgeoning', range: 'melee', properties: ['light'] },
    'dagger': { damage: '1d4', damageType: 'piercing', range: 'melee', properties: ['finesse', 'light', 'thrown'] },
    'greatclub': { damage: '1d8', damageType: 'bludgeoning', range: 'melee', properties: ['two-handed'] },
    'handaxe': { damage: '1d6', damageType: 'slashing', range: 'melee', properties: ['light', 'thrown'] },
    'javelin': { damage: '1d6', damageType: 'piercing', range: 'melee', properties: ['thrown'] },
    'light hammer': { damage: '1d4', damageType: 'bludgeoning', range: 'melee', properties: ['light', 'thrown'] },
    'mace': { damage: '1d6', damageType: 'bludgeoning', range: 'melee', properties: [] },
    'quarterstaff': { damage: '1d6', damageType: 'bludgeoning', range: 'melee', properties: ['versatile'], versatileDamage: '1d8' },
    'sickle': { damage: '1d4', damageType: 'slashing', range: 'melee', properties: ['light'] },
    'spear': { damage: '1d6', damageType: 'piercing', range: 'melee', properties: ['thrown', 'versatile'], versatileDamage: '1d8' },
    // Simple ranged
    'light crossbow': { damage: '1d8', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'loading', 'two-handed'] },
    'dart': { damage: '1d4', damageType: 'piercing', range: 'ranged', properties: ['finesse', 'thrown'] },
    'shortbow': { damage: '1d6', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'two-handed'] },
    'sling': { damage: '1d4', damageType: 'bludgeoning', range: 'ranged', properties: ['ammunition'] },
    // Martial melee
    'battleaxe': { damage: '1d8', damageType: 'slashing', range: 'melee', properties: ['versatile'], versatileDamage: '1d10' },
    'flail': { damage: '1d8', damageType: 'bludgeoning', range: 'melee', properties: [] },
    'glaive': { damage: '1d10', damageType: 'slashing', range: 'melee', properties: ['heavy', 'reach', 'two-handed'] },
    'greataxe': { damage: '1d12', damageType: 'slashing', range: 'melee', properties: ['heavy', 'two-handed'] },
    'greatsword': { damage: '2d6', damageType: 'slashing', range: 'melee', properties: ['heavy', 'two-handed'] },
    'halberd': { damage: '1d10', damageType: 'slashing', range: 'melee', properties: ['heavy', 'reach', 'two-handed'] },
    'lance': { damage: '1d12', damageType: 'piercing', range: 'melee', properties: ['reach'] },
    'longsword': { damage: '1d8', damageType: 'slashing', range: 'melee', properties: ['versatile'], versatileDamage: '1d10' },
    'maul': { damage: '2d6', damageType: 'bludgeoning', range: 'melee', properties: ['heavy', 'two-handed'] },
    'morningstar': { damage: '1d8', damageType: 'piercing', range: 'melee', properties: [] },
    'pike': { damage: '1d10', damageType: 'piercing', range: 'melee', properties: ['heavy', 'reach', 'two-handed'] },
    'rapier': { damage: '1d8', damageType: 'piercing', range: 'melee', properties: ['finesse'] },
    'scimitar': { damage: '1d6', damageType: 'slashing', range: 'melee', properties: ['finesse', 'light'] },
    'shortsword': { damage: '1d6', damageType: 'piercing', range: 'melee', properties: ['finesse', 'light'] },
    'trident': { damage: '1d6', damageType: 'piercing', range: 'melee', properties: ['thrown', 'versatile'], versatileDamage: '1d8' },
    'war pick': { damage: '1d8', damageType: 'piercing', range: 'melee', properties: [] },
    'warhammer': { damage: '1d8', damageType: 'bludgeoning', range: 'melee', properties: ['versatile'], versatileDamage: '1d10' },
    'whip': { damage: '1d4', damageType: 'slashing', range: 'melee', properties: ['finesse', 'reach'] },
    // Martial ranged
    'blowgun': { damage: '1', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'loading'] },
    'hand crossbow': { damage: '1d6', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'light', 'loading'] },
    'heavy crossbow': { damage: '1d10', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'heavy', 'loading', 'two-handed'] },
    'longbow': { damage: '1d8', damageType: 'piercing', range: 'ranged', properties: ['ammunition', 'heavy', 'two-handed'] },
    'net': { damage: '0', damageType: 'none', range: 'ranged', properties: ['thrown'] },
};
/**
 * Parse a weapon name from an equipment string.
 * Handles: "Longsword", "+1 Longsword", "Longsword +2", "Flametongue (Longsword)"
 * Returns the weapon stats or null if not recognized.
 */
export function getWeaponStats(equipmentName) {
    const lower = equipmentName.toLowerCase().trim();
    // Extract magic bonus: "+1", "+2", "+3"
    const magicMatch = lower.match(/\+(\d)/);
    const magicBonus = magicMatch ? parseInt(magicMatch[1], 10) : 0;
    // Strip magic bonus and parenthetical prefixes for matching
    const stripped = lower
        .replace(/\+\d\s*/g, '') // remove "+1 "
        .replace(/\(([^)]+)\)/g, '$1') // "Flametongue (Longsword)" -> "Flametongue Longsword"
        .trim();
    // Try exact match first
    if (SRD_WEAPONS[stripped]) {
        return { ...SRD_WEAPONS[stripped], name: equipmentName, magicBonus };
    }
    // Try matching against known weapon names (handles "Flame Tongue Longsword" etc.)
    for (const [weaponName, stats] of Object.entries(SRD_WEAPONS)) {
        if (stripped.includes(weaponName) || stripped.endsWith(weaponName)) {
            return { ...stats, name: equipmentName, magicBonus };
        }
    }
    return null;
}
/**
 * Get all weapons from a character's equipment list.
 */
export function getCharacterWeapons(equipment) {
    const weapons = [];
    for (const item of equipment) {
        // Equipment is typed string[] but legacy/imported data can contain objects;
        // skip anything that isn't a string so callers never crash on bad data.
        if (typeof item !== 'string')
            continue;
        const stats = getWeaponStats(item);
        if (stats)
            weapons.push(stats);
    }
    return weapons;
}
/**
 * Calculate the attack bonus for a weapon.
 */
export function getWeaponAttackBonus(weapon, abilityScores, proficiencyBonus, getModifierFn) {
    const abilityMod = getWeaponAbilityMod(weapon, abilityScores, getModifierFn);
    return abilityMod + proficiencyBonus + weapon.magicBonus;
}
/**
 * Get the ability modifier used for a weapon (handles finesse).
 */
export function getWeaponAbilityMod(weapon, abilityScores, getModifierFn) {
    if (weapon.abilityOverride) {
        return getModifierFn(abilityScores[weapon.abilityOverride]);
    }
    if (weapon.properties.includes('finesse')) {
        const strMod = getModifierFn(abilityScores.strength);
        const dexMod = getModifierFn(abilityScores.dexterity);
        return Math.max(strMod, dexMod);
    }
    if (weapon.range === 'ranged') {
        return getModifierFn(abilityScores.dexterity);
    }
    return getModifierFn(abilityScores.strength);
}
/**
 * Get the damage notation for a weapon (e.g. "1d8+3 slashing").
 */
export function getWeaponDamageNotation(weapon, abilityScores, getModifierFn, versatile = false) {
    const abilityMod = getWeaponAbilityMod(weapon, abilityScores, getModifierFn);
    const totalMod = abilityMod + weapon.magicBonus;
    const dice = versatile && weapon.versatileDamage ? weapon.versatileDamage : weapon.damage;
    const modStr = totalMod >= 0 ? `+${totalMod}` : `${totalMod}`;
    return `${dice}${modStr}`;
}
/** Parse a bare weapon dice expression like "2d6" into its count + size. Returns null if malformed. */
export function parseWeaponDice(dice) {
    const match = dice.trim().match(/^(\d+)d(\d+)$/i);
    if (!match)
        return null;
    return { dieCount: parseInt(match[1], 10), dieSize: parseInt(match[2], 10) };
}
/**
 * Build a structured WeaponDamage payload for a given weapon + attack context.
 *
 * The returned `dice`/`modifier` are the BASE (non-crit) values. Crit-doubling
 * is applied at roll time by the UI so the same payload can be re-displayed.
 *
 * @param extraModifier additional flat damage (e.g. echo-knight damage mod, rage)
 */
export function buildWeaponDamage(weapon, abilityScores, getModifierFn, isCrit, versatile = false, extraModifier = 0) {
    const dice = versatile && weapon.versatileDamage ? weapon.versatileDamage : weapon.damage;
    const parsed = parseWeaponDice(dice);
    if (!parsed)
        return null; // e.g. "net" (0 damage) / "blowgun" (flat 1) — no roll button
    const abilityMod = getWeaponAbilityMod(weapon, abilityScores, getModifierFn);
    return {
        weaponName: weapon.name,
        dice,
        dieCount: parsed.dieCount,
        dieSize: parsed.dieSize,
        modifier: abilityMod + weapon.magicBonus + extraModifier,
        damageType: weapon.damageType,
        isCrit,
    };
}
/**
 * Compute the crit-doubled damage expression per the 5e rule:
 * roll DOUBLE the dice, add the modifier ONCE.
 * Non-crit returns the base expression unchanged.
 *
 * @returns the dice expression to roll (modifier applied separately by caller)
 */
export function getCritDamageDice(wd) {
    const count = wd.isCrit ? wd.dieCount * 2 : wd.dieCount;
    return `${count}d${wd.dieSize}`;
}
//# sourceMappingURL=weapon-utils.js.map